/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/

import * as loginActions from ".";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  LoadStatus,
  LoadingState,
  User,
  UserAccessToken,
  UserRole,
} from "../../../types";

export interface UseWithUser {
  user?: User;
  accessToken?: string;
  loginStatus: LoadingState;

  login: (username: string, password: string) => Promise<UserAccessToken>;
  signup: (
    username: string,
    password: string,
    email: string
  ) => Promise<UserAccessToken>;
  verifyToken: (token: string) => Promise<UserAccessToken>;
  logout: () => Promise<void>;

  validateUsername: (v: string) => string;
  validateEmail: (v: string) => string;
  validatePassword: (v: string) => string;
  userCanEdit: () => boolean;
  userCanManageUsers: () => boolean;
}

export default function useWithUser(): UseWithUser {
  const { user, accessToken, loginStatus } = useAppSelector(
    (state) => state.login
  );
  const { organizations } = useAppSelector((state) => state.organizations);
  const dispatch = useAppDispatch();

  async function login(
    username: string,
    password: string
  ): Promise<UserAccessToken> {
    if (loginStatus.status === LoadStatus.IN_PROGRESS) {
      throw new Error("Already logging in");
    }
    return await dispatch(loginActions.login({ username, password })).unwrap();
  }

  async function signup(
    name: string,
    password: string,
    email: string
  ): Promise<UserAccessToken> {
    if (loginStatus.status === LoadStatus.IN_PROGRESS) {
      throw new Error("Already logging in");
    }
    return await dispatch(
      loginActions.signup({ name, password, email })
    ).unwrap();
  }

  async function verifyToken(token: string): Promise<UserAccessToken> {
    return await dispatch(loginActions.verifyToken(token)).unwrap();
  }

  async function logout(): Promise<void> {
    if (!user) return;
    await dispatch(loginActions.logout());
  }

  function validateUsername(v: string): string {
    if (!v || v.length < 4 || v.length > 16) {
      return "Usernames must be 4-16 characters long";
    }
    return "";
  }

  function validateEmail(v: string): string {
    const splitEmail = v.split("@");
    if (
      splitEmail.length !== 2 ||
      splitEmail[0].length > 64 ||
      splitEmail[1].length > 255
    ) {
      return "Please enter a valid email";
    }
    return "";
  }

  function validatePassword(v: string): string {
    if (!v || v.length < 8 || v.length > 16) {
      return "Passwords must be 8-16 characters long";
    }
    const PASSWORD_REGEX = new RegExp(
      "^(?=.*[0-9])(?=.*[a-zA-Z])(?=.*(\\W))(?!.* ).{8,16}$"
    );
    if (!PASSWORD_REGEX.test(v)) {
      return "Passwords must contain 1+ character, 1+ symbol, and 1+ number";
    }
    return "";
  }

  function userCanEdit(item?: {
    org?: string;
    hidden?: boolean;
    members?: { user: string; role: UserRole }[];
  }): boolean {
    if (!user) return false;
    if (
      user.userRole === UserRole.CONTENT_MANAGER ||
      user.userRole === UserRole.ADMIN
    ) {
      return true;
    }
    // item belongs to an organization
    if (item && item.org) {
      const org = organizations[item.org];
      if (
        org &&
        org.members.find(
          (m) =>
            m.user === user.id &&
            (m.role === UserRole.CONTENT_MANAGER || m.role === UserRole.ADMIN)
        )
      ) {
        return true;
      }
    }
    if (
      item &&
      item.members &&
      item.members.find(
        (m) =>
          m.user === user.id &&
          (m.role === UserRole.CONTENT_MANAGER || m.role === UserRole.ADMIN)
      )
    ) {
      return true;
    }
    return false;
  }

  function userCanManageUsers(): boolean {
    if (!user) return false;
    if (user.userRole === UserRole.ADMIN) return true;
    for (const org of Object.values(organizations)) {
      if (
        org.members.find((u) => u.user === user.id && u.role === UserRole.ADMIN)
      ) {
        return true;
      }
    }
    return false;
  }

  return {
    user,
    accessToken,
    loginStatus,
    login,
    signup,
    verifyToken,
    logout,
    validateUsername,
    validateEmail,
    validatePassword,
    userCanEdit,
    userCanManageUsers,
  };
}
